import { ActionAfterViewed } from '@cian/action-after-viewed-component';

import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Carousel } from 'shared/packages/Carousel';
import { IOfferSnippetSchema } from 'shared/repositories/commercial-search-offers/entities/offer_snippet/OfferSnippetSchema';
import { ISpecialPromoBuilderOffersBuilderSchema } from 'shared/repositories/commercial-search-offers/entities/special_promo_builder_offers_builder/SpecialPromoBuilderOffersBuilderSchema';
import { selectIsSerpFetching } from 'shared/selectors/selectIsSerpFetching';
import { extractUndefined } from 'shared/utils/extractUndefined';

import { SpecialProjectBuilder } from './internal/components/SpecialProjectBuilder';
import { SpecialProjectCarouselLayout } from './internal/components/SpecialProjectCarouselLayout';
import { SpecialProjectCarouselSlide } from './internal/components/SpecialProjectCarouselSlide';
import { SPECIAL_PROJECT_CAROUSEL_OPTIONS } from './internal/constants';
import { trackBuilderButtonClick } from './internal/tracking/trackBuilderButtonClick';
import { trackBuilderLogoClick } from './internal/tracking/trackBuilderLogoClick';
import { trackOfferClick } from './internal/tracking/trackOfferClick';
import { trackParentClick } from './internal/tracking/trackParentClick';
import { trackPromoBuilderOffersShow } from './internal/tracking/trackPromoBuilderOffersShow';
import { trackSlideChange } from './internal/tracking/trackSlideChange';

export type SpecialProjectCarouselProps = {
  builder: ISpecialPromoBuilderOffersBuilderSchema;
  offers: IOfferSnippetSchema[];
  allOffersUrl: string;
};

export const SpecialProjectCarousel: FC<SpecialProjectCarouselProps> = props => {
  const { builder, offers, allOffersUrl } = props;

  const isFetching = useSelector(selectIsSerpFetching);

  const { realtyUserId, cardUrl: builderCardUrl } = builder;

  const [shownSlides, setShownSlides] = useState<number[]>([]);

  const title = `Предложения от застройщика ${builder.name}`;

  const onSlideChangeHandle = useCallback(
    (activeSlideIndex: number) => {
      const newSlides = offers
        .map(offer => offer.id)
        .slice(activeSlideIndex, activeSlideIndex + SPECIAL_PROJECT_CAROUSEL_OPTIONS.slidesPerView);

      const isPossibleSendEvent = !!newSlides.filter(newSlide => !shownSlides.includes(newSlide)).length;

      if (!isPossibleSendEvent) {
        return;
      }

      setShownSlides(oldSlides => [...new Set([...oldSlides, ...newSlides])]);

      trackSlideChange({
        realtyUserId,
        activeSlideIndex,
        offers,
        title,
        builderCardUrl,
      });
    },
    [offers, realtyUserId, title, builderCardUrl, shownSlides],
  );

  const slides = useMemo(() => {
    return offers.map((offer, index) => (
      <SpecialProjectCarouselSlide
        key={offer.id}
        newbuilding={extractUndefined(offer.newbuilding)}
        underground={extractUndefined(offer.underground)}
        {...offer}
        onParentClick={() =>
          trackParentClick({
            realtyUserId,
            position: index,
            parentId: offer.newbuilding?.id,
            parentUrl: offer.newbuilding?.url,
            title,
          })
        }
        onSlideClick={() =>
          trackOfferClick({
            realtyUserId,
            position: index,
            offerId: offer.id,
            cardUrl: offer.cardUrl,
            title,
          })
        }
      />
    ));
  }, [offers, realtyUserId, title]);

  const onAfterInitHandle = useCallback(() => {
    trackPromoBuilderOffersShow({
      realtyUserId,
      title,
      builderCardUrl,
    });

    const newSlides = offers.map(offer => offer.id).slice(0, SPECIAL_PROJECT_CAROUSEL_OPTIONS.slidesPerView);

    setShownSlides(newSlides);
  }, [realtyUserId, title, builderCardUrl, offers]);

  const carousel = useMemo(() => {
    return (
      <SpecialProjectCarouselLayout
        builder={
          <SpecialProjectBuilder
            allOffersUrl={allOffersUrl}
            title={title}
            onButtonClick={() =>
              trackBuilderButtonClick({
                realtyUserId,
                builderAllOffersUrl: allOffersUrl,
                title,
              })
            }
            onLogoClick={() =>
              trackBuilderLogoClick({
                realtyUserId,
                builderCardUrl,
                title,
              })
            }
            {...builder}
          />
        }
        carousel={
          <Carousel
            className="special-project-carousel"
            swiperOptions={{
              ...SPECIAL_PROJECT_CAROUSEL_OPTIONS,
              onSlideChange: swiper => onSlideChangeHandle(swiper.activeIndex),
            }}
          >
            {slides}
          </Carousel>
        }
        offersFewed={offers.length < SPECIAL_PROJECT_CAROUSEL_OPTIONS.slidesPerView}
      />
    );
  }, [allOffersUrl, title, builder, slides, offers.length, realtyUserId, builderCardUrl, onSlideChangeHandle]);

  if (isFetching) {
    return carousel;
  }

  return (
    <ActionAfterViewed callback={onAfterInitHandle} percentVisible={0.5}>
      {carousel}
    </ActionAfterViewed>
  );
};
